import { isArray, isObject } from 'lodash';

export function getInputNamesValue(target, is_array = false) {
  let data = {};
  let array = [];
  function nameValueObject(element) {
    let name = element.name;
    let value = element.value;
    if (name in data) {
      data[name].push(value);
    } else {
      data[name] = [value];
    }
  }
  function nameArray(element) {
    let name = element.name;
    if (array.indexOf(name) == -1) {
      array.push(name);
    }
  }
  const funct = is_array ? nameArray : nameValueObject;
  const resp = is_array ? array : data;
  if (isArray(target) || isObject(target)) {
    target.forEach(funct);
  } else {
    funct(target);
  }
  return resp;
}

/**
 * @function getFieldSelector
 * Utility method for getting the selector for the specified field
 *
 * @param {object} field contains the field props
 * @param {input|select} field.tagName HTML dom element nam
 * @param {string} field.name HTML select/input unique field name prop
 * @param {string} field.type HTML select/input unique field type prop
 * @param {boolean} [checked=false] whether selector should be for checked fields or not
 *
 * @returns {string} containing the selector for the field
 */
export function getFieldSelector({ tagName, type, name, checked = false }) {
  if (!tagName || !name || (tagName.toLowerCase() !== 'select' && !type)) {
    return null;
  }
  tagName = tagName.toLowerCase();
  let selector = null;

  if (tagName === 'select') {
    selector = `${tagName}[name="${name}"]`;
  } else if (tagName === 'input' && (type === 'checkbox' || type === 'radio')) {
    selector = `${tagName}[type="${type}"][name="${name}"]${
      checked ? ':checked' : ''
    }`;
  }

  return selector;
}

/**
 * @function getValidFieldValues
 * Utility method for getting the valid values for the fields otherwise returns null
 * i.e.,
 *    for checkboxes = array,
 *    for radio      = single,
 *    for select     = array incase of multiple otherwise single,
 *
 * @param {array} fieldValues contains the list of field props
 * @param {input|select} fieldValues[].tagName HTML dom element nam
 * @param {string} fieldValues[].name HTML select/input unique field name prop
 * @param {string} fieldValues[].type HTML select/input unique field type prop
 * @param {string|array} fieldValues[].values for the fields
 *
 * @returns {array} containing the fields with valid values
 */
export function getValidFieldValues(fieldValues = []) {
  const validFieldValues = [];

  for (const field of fieldValues) {
    let { tagName, type, name, values } = field;
    let validValues = values || null;
    tagName = tagName && tagName.toLowerCase();

    if (
      !tagName ||
      !name ||
      !values ||
      (tagName.toLowerCase() !== 'select' && !type)
    ) {
      validValues = null;
      validFieldValues.push({ ...field, values: validValues });
      continue;
    }

    if (tagName === 'input') {
      switch (type) {
        case 'checkbox':
          validValues = Array.isArray(values) ? values : [values];
          break;
        case 'radio':
          validValues = Array.isArray(values) ? null : values;
          break;
      }
    }

    if (tagName === 'select') {
      const selector = `select[name=${name}]`;
      const $dropdown = $(selector);
      const multiple = $dropdown.prop('multiple');

      if (multiple) {
        validValues = Array.isArray(values) ? values : [values];
      } else {
        validValues = !Array.isArray(values) ? values : null;
      }
    }

    validFieldValues.push({ ...field, values: validValues });
  }

  return validFieldValues;
}

/**
 * @function getFieldValues
 * Utility method for getting the value from the field
 *
 * @param {object} field contains the field props
 * @param {input|select} field.tagName HTML dom element nam
 * @param {string} field.name HTML select/input unique field name prop
 * @param {string} field.type HTML select/input unique field type prop
 *
 * @returns {string} containing the value for the field
 */
export function getFieldValues({
  tagName = null,
  type = null,
  name = null,
} = {}) {
  let selector;

  if (!tagName || !name || (tagName.toLowerCase() !== 'select' && !type)) {
    return null;
  }

  tagName = tagName.toLowerCase();

  if (tagName === 'select') {
    selector = `${tagName}[name="${name}"]`;
  } else if (tagName === 'input' && (type === 'checkbox' || type === 'radio')) {
    selector = `${tagName}[type="${type}"][name="${name}"]:checked`;
  } else {
    return null;
  }

  if (tagName === 'select') {
    return $(selector).val();
  }

  if (tagName === 'input' && type === 'checkbox') {
    return $(selector)
      .map(function () {
        return $(this).val();
      })
      .get();
  }

  if (tagName === 'input' && type === 'radio') {
    return $(selector).val();
  }

  return null;
}

/**
 * @function setFieldValues
 * Utility method for setting the values to the fields after validating
 *
 * @param {array} fieldValues contains the list of field props
 * @param {input|select} fieldValues[].tagName HTML dom element nam
 * @param {string} fieldValues[].name HTML select/input unique field name prop
 * @param {string} fieldValues[].type HTML select/input unique field type prop
 * @param {string|array} fieldValues[].values for the fields
 *
 * @returns {array} containing the fields with valid values
 */
export function setFieldValues(fieldValues) {
  const validFieldValues = getValidFieldValues(fieldValues);

  for (const field of validFieldValues) {
    let { tagName, type, name, values = null } = field;
    tagName = tagName && tagName.toLowerCase();

    if (!tagName || !name || (tagName.toLowerCase() !== 'select' && !type)) {
      continue;
    }

    const fieldSelector = getFieldSelector({ tagName, type, name });

    if (tagName === 'input' && (type === 'checkbox' || type === 'radio')) {
      $(fieldSelector).each(function () {
        const $elem = $(this);
        const value = $elem.val();
        const checked =
          (Array.isArray(values) && values.includes(value)) || values === value;

        $elem.prop('checked', checked);
      });
    }

    if (tagName === 'select') {
      const $elem = $(fieldSelector);
      let defaultValue = $elem.find('option[selected]').val();
      const selectedValues =
        values || defaultValue || $elem.find('option').first().val();
      $elem.selectpicker('val', selectedValues);
    }
  }
}
